import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api } from '@/services/api';
import { useToast } from '@/components/ui/use-toast';
import type { UpdateAiSettingsRequest } from '@/types';

export function AiSettingsPage() {
  const { toast } = useToast();
  const { data, isLoading } = useQuery({
    queryKey: ['ai-settings'],
    queryFn: () => api.getAiSettings(),
  });

  const [form, setForm] = useState<UpdateAiSettingsRequest>({
    provider: 'openai',
  });

  useEffect(() => {
    if (!data) return;
    setForm({
      enableAiDocumentQa: data.enableAiDocumentQa,
      enableAiPrisonerDataQa: data.enableAiPrisonerDataQa,
      enableAiHybridQa: data.enableAiHybridQa,
      provider: data.provider,
      openaiModel: data.openaiModel,
      openaiEmbeddingModel: data.openaiEmbeddingModel,
      apiBaseUrl: data.apiBaseUrl || '',
      allowDocumentScope: data.allowDocumentScope,
      allowPrisonerScope: data.allowPrisonerScope,
    });
  }, [data]);

  const mutation = useMutation({
    mutationFn: (payload: UpdateAiSettingsRequest) => api.updateAiSettings(payload),
    onSuccess: () => {
      toast({
        title: 'KI-Einstellungen gespeichert',
        description: 'Die Änderungen wurden erfolgreich übernommen.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Speichern fehlgeschlagen',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const updateBool = (key: keyof UpdateAiSettingsRequest) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [key]: event.target.checked }));
  };

  if (isLoading) {
    return <p className="text-muted-foreground">Lade KI-Einstellungen...</p>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">KI-Einstellungen</h1>
        <p className="text-muted-foreground">
          Konfiguration für Dokumenten-QA, Stammdaten-QA und KI-Provider.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Features</CardTitle>
          <CardDescription>Steuere, welche KI-Funktionen verfügbar sind.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={Boolean(form.enableAiDocumentQa)}
              onChange={updateBool('enableAiDocumentQa')}
            />
            Dokumenten-QA aktivieren
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={Boolean(form.enableAiPrisonerDataQa)}
              onChange={updateBool('enableAiPrisonerDataQa')}
            />
            Gefangenen-/Stammdaten-QA aktivieren
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={Boolean(form.enableAiHybridQa)}
              onChange={updateBool('enableAiHybridQa')}
            />
            Hybride KI-Abfragen aktivieren
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={Boolean(form.allowDocumentScope)}
              onChange={updateBool('allowDocumentScope')}
            />
            Scope „Dokumente“ zulassen
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={Boolean(form.allowPrisonerScope)}
              onChange={updateBool('allowPrisonerScope')}
            />
            Scope „Gefangene“ zulassen
          </label>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Provider</CardTitle>
          <CardDescription>OpenAI oder OpenRouter (z. B. hunter Alpha).</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium">Provider</label>
            <select
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              value={form.provider || 'openai'}
              onChange={(event) => {
                const provider = event.target.value as 'openai' | 'openrouter';
                setForm((prev) => ({
                  ...prev,
                  provider,
                  openaiModel:
                    provider === 'openrouter'
                      ? prev.openaiModel || 'hunter-alpha'
                      : prev.openaiModel || 'gpt-4o-mini',
                  apiBaseUrl:
                    provider === 'openrouter'
                      ? prev.apiBaseUrl || 'https://openrouter.ai/api/v1'
                      : prev.apiBaseUrl || 'https://api.openai.com/v1',
                }));
              }}
            >
              <option value="openai">OpenAI</option>
              <option value="openrouter">OpenRouter</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">
              Modell ({form.provider === 'openrouter' ? 'OpenRouter' : 'OpenAI'})
            </label>
            <Input
              value={form.openaiModel || ''}
              onChange={(event) => setForm((prev) => ({ ...prev, openaiModel: event.target.value }))}
              placeholder={form.provider === 'openrouter' ? 'hunter-alpha' : 'gpt-4o-mini'}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">OpenAI Embedding Model</label>
            <Input
              value={form.openaiEmbeddingModel || ''}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, openaiEmbeddingModel: event.target.value }))
              }
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">API Base URL (optional)</label>
            <Input
              value={form.apiBaseUrl || ''}
              onChange={(event) => setForm((prev) => ({ ...prev, apiBaseUrl: event.target.value }))}
              placeholder={
                form.provider === 'openrouter'
                  ? 'https://openrouter.ai/api/v1'
                  : 'https://api.openai.com/v1'
              }
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">
              {form.provider === 'openrouter' ? 'OpenRouter API Key' : 'OpenAI API Key'} (neu setzen)
            </label>
            <Input
              type="password"
              value={form.openaiApiKey || ''}
              onChange={(event) => setForm((prev) => ({ ...prev, openaiApiKey: event.target.value }))}
              placeholder={
                data?.openaiApiKeyConfigured
                  ? 'Bereits hinterlegt (maskiert)'
                  : form.provider === 'openrouter'
                    ? 'or-...'
                    : 'sk-...'
              }
            />
            {data?.openaiApiKeyConfigured && (
              <p className="mt-1 text-xs text-muted-foreground">
                API-Key ist hinterlegt
                {data.openaiApiKeyLastUpdatedAt
                  ? ` (aktualisiert: ${data.openaiApiKeyLastUpdatedAt.slice(0, 10)})`
                  : ''}
                .
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={() => mutation.mutate(form)} disabled={mutation.isPending}>
          {mutation.isPending ? 'Speichert...' : 'Einstellungen speichern'}
        </Button>
      </div>
    </div>
  );
}
